import {
    AppBar, Box,
    Button,
    Container,
    Grid,
    IconButton,
    Select,
    Toolbar,
    useMediaQuery,
    Theme,
    MenuItem as SelectOption
} from "@mui/material";

import { useTheme } from '@mui/material/styles';
import {useDispatch} from "react-redux";
import Head from "next/head";
import Link from "next/link";
import {FunctionComponent, useEffect, useState} from "react";
import styled from "styled-components";
import {appSelector} from "../redux/app/selectors";
import {actions as appActions} from "../redux/app";
import LocationIcon from "../components/icons/LocationIcon";
import PhoneIcon from "../components/icons/PhoneIcon";
import EnquiryModal from "../components/blockComponents/EnquiryModal";
import {Close as CloseIcon, Menu as MenuIcon} from "@mui/icons-material";
import {media} from "../../styles/styles";
import PipaGate from "./blocks/PipaGate";
import MobileMenu from "./blockComponents/MobileMenu";
import {AllPages} from "../types";
import {Event} from '../../utils/gtm';

import {useRouter} from "next/router";
import {FbEvent} from '../../utils/fbq';
import {UnknownAction} from "redux";
import Image from "next/image";

interface MenuItemProps {
    isLogo?: boolean;
    isOrange?: boolean;
}

interface MenuButtonProps {
    active: boolean;
}

const StyledOutlinedButton = styled(Button)`
    && {
        padding: 7px 30px;
        border-color: #de4a22;
        margin-bottom: 15px;
        font-size: 1.2em;

        &:hover {
            background-color: #de4a22;
            border-color: #de4a22;
        }
    }
`;
const BodyContent = styled.div`
    margin-top: 93px;
    min-height: 300px;
    ${media.phone`min-height:auto;`}
`;
const CopyrightFooter = styled.div`
    text-align: center;
    padding: 0.3em 0;

    a {
        color: #de4a22;
        margin: 0 0.5em;
    }
`;
const LocationSelectWrapper = styled.div`
    background-color: #324037;
    border-radius: 0 0 6px 6px;
    padding: 0 2em;

    ${media.phone`
     padding:0 .5em;
  `};
`;
const LogoWrapper = styled.div`
    margin-top: -10px;
    ${media.phone`
margin-left:10px;
  `}
`;
const Wrapper = styled.div`
    display: flex;
    flex-grow: 1;
    justify-content: center;
    flex-direction: column;
`;
const Footer = styled.footer`
    background-color: #252f28;
    color: #fff;
`;
const FooterGrid = styled(Grid)`
    padding: 3rem 0;
`;
const LocationSelect = styled(Select)`
    appearance: none;
    background-color: #324037;
    color: #fff;
    border: none;
    cursor: pointer;

    & > .MuiSelect-select {
        padding: 0 10px;
        padding-right: 0 !important;
        color: #fff;
        font-size: 12px;
    }

    fieldset {
        border: none;
    }
`;
const LocationOption = styled(SelectOption)`
    display: block;
    min-height: auto;
`;

const MenuButton = styled(Button)<MenuButtonProps>`
    && {
        padding: 8px 1rem 19px;
        margin: 0 4px;
        border-radius: 4px 4px 0 0;
        font-size: 1.2em;
        border-bottom: ${({active}) =>
                active ? `4px solid #de4a22` : `4px solid transparent`};

        &:hover {
            background-color: initial;
            border-bottom: 4px solid #de4a22;
        }

        &:last-child {
            margin-right: 0.5rem;
        }
    }
`;
const MobileMenuWrapper = styled.div`
    color: #fff;
    text-align: center;
    position: relative;
    font-size: 30px;

    .MuiIconButton-root {
        padding: 0 12px 12px;
    }

    > div {
        font-size: 14px;
        position: absolute;
        bottom: -5px;
        left: 0;
        width: 100%;
    }
`;
const MenuItem = styled('span').withConfig({
    shouldForwardProp: (prop) => !['isOrange', 'isLogo'].includes(prop)
})<MenuItemProps>`
    display: block;
    font-size: 1.3rem;
    font-weight: bold;
    margin: 1rem 0;

    .phone_number {
        &:hover {
            color: #E55934;
            text-decoration: underline;
        }
    }

    ${({isOrange}) =>
            isOrange &&
            `
    color: #E55934;
`}
    ${({isLogo}) =>
            isLogo &&
            `
    text-align: right;
    margin:60px 0 0;
`}
    ${({isLogo}) => isLogo && media.phone`margin:0px;`}
`;
const PaddlessButton = styled(Button)`
    && {
        padding: 0;
        line-height: 23px;
        font-size: 12px;
    }
`;
const StyledLogo = styled.img`
    width: 210px;
    height: 49px;
`;
const FooterLogo = styled.img`
    width: 100%;
    max-width: 200px;
`;
const useStyles = ((theme: Theme) => ({
    primaryLight: {
        backgroundColor: theme.palette?.primary?.light || `#F0F0F0`,
    },
    primaryDark: {
        backgroundColor: theme.palette?.primary?.dark || `#252F28`,
    },
    secondaryLight: {
        backgroundColor: theme.palette?.secondary?.light || `#333333DD`,
        color: "#fff",
    },
    secondaryDark: {
        backgroundColor: theme.palette?.secondary?.dark || `#DE4A22`,
    },
    appbar: {
        [theme.breakpoints.up("sm")]: {
            justifyContent: "center",
            minHeight: 33,
        },
        position: "relative",
        alignItems: "flex-start",
        minHeight: 33,
    },
    phoneLink: {
        position: "absolute",
        right: 28,
    },
    upperAppbar: {
        zIndex: 1299,
    },
    lowerAppbar: {
        alignItems: "flex-start",
        maxHeight: 60,
        minHeight: 60,
    },
}));

interface Props {
    hasPipaGate?: boolean;
    title: string;
    allPages: AllPages;
    children: any
}

const Layout: FunctionComponent<Props> = ({
                                              children,
                                              hasPipaGate,
                                              title,
                                              allPages,
                                          }) => {
    const router = useRouter();
    const matches = useMediaQuery("(max-width:600px)");
    const theme = useTheme();
    const classes = useStyles(theme);
    const app = appSelector((state) => state.app);
    const dispatch = useDispatch();
    const handleChangeLocationState = ({target: {value}}) => {
        dispatch(appActions.updateLocationState(value));
    };
    const [anchorEl, setAnchorEl] = useState(null);
    const handleClick = (event) => {
        setAnchorEl(true);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };
    const statesAvailable = [
        {longName: "Queensland", shortName: "QLD"},
        {longName: "New South Wales", shortName: "NSW"},
        {longName: "South Australia", shortName: "SA"},
        {longName: "Victoria", shortName: "VIC"},
        {longName: "Tasmania", shortName: "TAS"},
        {longName: "Western Australia", shortName: "WA"},
        {longName: "Australian Capital Teritory", shortName: "ACT"},
        {longName: "International", shortName: "INTL"},
    ];

    useEffect(() => {
        if (!app.locationState.fetched)  {
            dispatch(appActions.fetchLocationThunk() as unknown as UnknownAction);
        }
    }, []);
    const [open, setOpen] = useState(false);
    return (
        <Wrapper>
            <Head>
                <title>{`Emergency Services${title ? ` | ${title}` : ``}`}</title>
            </Head>
            <AppBar
                position="fixed"
                sx={{...classes.primaryDark, ...classes.upperAppbar}}
            >
                <Container maxWidth="lg" disableGutters>
                    <Toolbar sx={classes.appbar}>
                        <LocationSelectWrapper>
                            <LocationIcon/>
                            <LocationSelect
                                onChange={handleChangeLocationState}
                                value={app.locationState.shortName}
                                variant="outlined"
                                IconComponent={() => null}
                                open={open}
                                onClose={() => setOpen(false)}
                                onOpen={() => setOpen(true)}
                            >
                                {statesAvailable.map((state) => (
                                    <LocationOption key={state.shortName} value={state.shortName}>
                                        {matches ? state.shortName : state.longName}
                                    </LocationOption>
                                ))}
                            </LocationSelect>
                            <PaddlessButton
                                variant="text"
                                color="primary"
                                disableElevation
                                onClick={() => setOpen(!open)}
                            >
                                <u>change</u>
                            </PaddlessButton>
                            <Button
                                variant="text"
                                color="primary"
                                startIcon={<PhoneIcon/>}
                                sx={classes.phoneLink}
                                href={`tel:${app.phoneNumber}`}
                            >
                                <span onClick={() => {
                                    FbEvent('Contact');
                                    Event({
                                        action: 'click',
                                        category: 'phone call tracking',
                                        event_label: 'Phone Call',
                                        event_action: 'Click to Call'
                                    })
                                }}>1800-954-754</span>
                            </Button>
                        </LocationSelectWrapper>
                    </Toolbar>
                </Container>
                <Container maxWidth="lg" disableGutters>
                    <Toolbar disableGutters sx={classes.lowerAppbar}>
                        <LogoWrapper>
                            <Link href={"/"}>
                                <span>
                                    <Image src={`/images/Logo.png`} alt={`Logo`}
                                           width="210"
                                           height="49" />
                                </span>
                            </Link>
                        </LogoWrapper>
                        <div style={{flexGrow: 1}}/>
                        <Box sx={(theme) => ({[theme.breakpoints.down("sm")]: {display: `none`}, display: `flex`})}>
                            {allPages.navigation.fields.pageLinks.map((link, index) => (
                                <Link href={link.fields.linkUrl} key={index}>
                                    <span style={{display: 'flex'}}>
                                        <MenuButton
                                            variant="text"
                                            color="primary"
                                            active={
                                                (router.asPath &&
                                                router.asPath === `/${link.fields.linkUrl}` ? true : undefined)
                                            }
                                            disableElevation
                                            style={{
                                                width: `100%`,
                                                paddingLeft: `1rem`,
                                                paddingRight: `1rem`
                                            }}
                                        >
                                            {link.fields.linkTitle}
                                        </MenuButton>
                                    </span>
                                </Link>
                            ))}
                        </Box>
                        <Box sx={(theme) => ({[theme.breakpoints.down("sm")]: {display: `none`}})}>
                            <Link href="/contact-us" >
                                <StyledOutlinedButton
                                    variant="outlined"
                                    color="primary"
                                    disableElevation
                                >
                                    Contact us now
                                </StyledOutlinedButton>
                            </Link>
                        </Box>
                        <Box sx={(theme) => ({[theme.breakpoints.up("md")]: {display: `none`}})}>
                            <MobileMenuWrapper>
                                <IconButton
                                    aria-label="menu"
                                    onClick={handleClick}
                                    color="primary"
                                    sx={{fontSize:`1.5rem`}}
                                >
                                    {!!anchorEl ? (
                                        <CloseIcon fontSize="inherit"/>
                                    ) : (
                                        <MenuIcon fontSize="inherit"/>
                                    )}
                                </IconButton>
                                <div>{!!anchorEl ? "Close" : "Menu"}</div>
                            </MobileMenuWrapper>
                        </Box>
                    </Toolbar>
                </Container>
            </AppBar>
            <MobileMenu
                handleClose={handleClose}
                anchorEl={anchorEl}
                navigation={allPages.navigation}
            />
            <BodyContent>{children}</BodyContent>
            {hasPipaGate && (
                <PipaGate
                    open={
                        !app.pipaAccepted &&
                        (app.locationState.shortName === "QLD" ||
                            app.locationState.shortName === "WA")
                    }
                />
            )}
            <EnquiryModal operatingTimes={allPages.operatingTimes}/>
            <Footer>
                <Container maxWidth="lg">
                    <FooterGrid container direction="row" justifyContent="space-between">
                        <Grid item sm={6} md={3}>
                            <MenuItem
                                isOrange
                                onClick={() => dispatch(appActions.toggleModal())}
                            >
                                Contact us now
                            </MenuItem>
                            <MenuItem>
                                or call{` `}
                                <a className="phone_number" href={`tel:${app.phoneNumber}`} onClick={() => {
                                    FbEvent('Contact');
                                    Event({
                                        action: 'click',
                                        category: 'phone call tracking',
                                        event_label: 'Phone Call',
                                        event_action: 'Click to Call'
                                    })
                                }}>{app.phoneNumber}</a>
                            </MenuItem>
                        </Grid>
                      <Grid item sm={6} md={3}>
                            {allPages.navigation.fields.pageLinks.map((link, index) => (
                                <Link href={link.fields.linkUrl} key={index}>
                                    <span>
                                        <MenuItem>{link.fields.linkTitle}</MenuItem>
                                    </span>
                                </Link>
                            ))}
                        </Grid>
                      <Grid item sm={12} md={6}>
                            <MenuItem isLogo>
                                <a
                                    href="https://www.shine.com.au"
                                    target="_blank"
                                    rel="noopener"
                                >
                                    <div style={{fontSize: 14, fontWeight: 400}}>
                                        Brought to you by
                                    </div>
                                    <FooterLogo
                                        src={"/images/shine-logo.png"}
                                        alt="Shine Lawyers logo"
                                    />
                                </a>
                            </MenuItem>
                        </Grid>
                    </FooterGrid>
                    <CopyrightFooter>
                        © Copyright {new Date().getFullYear()} Shine Lawyers. All Rights Reserved.{" "}
                        <Link href="https://www.shine.com.au/privacy-policy">Privacy policy</Link>{" "}
                        <Link
                            href="https://www.shine.com.au/terms-and-conditions"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Terms and conditions
                        </Link>{" "}
                        <Link href="/sitemap">Sitemap</Link>
                    </CopyrightFooter>
                </Container>
            </Footer>
        </Wrapper>
    );
};

export default Layout;
